import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Loader2, Search, Check, X } from "lucide-react";
import { toDatetimeLocalValue } from '../utils/timezone';
import { calculateTipCommission, getTipRoleFromEmployeeRole } from '../utils/tipCommission';

const OTHER_TIP_ROLES = ['Cashier', 'Hosts', 'Floor Supervisor'];

const resolveOtherTipRole = (roleString) => {
  if (!roleString) return 'Cashier';
  const roles = String(roleString).split(',').map((r) => r.trim()).filter(Boolean);
  const match = OTHER_TIP_ROLES.find((role) => roles.includes(role));
  return match || roles[0] || 'Cashier';
};

const OtherTipsEditModal = ({ isOpen, onClose, record, onUpdate, employees, currentUser }) => {
  const [formData, setFormData] = useState({
    date: '',
    empname: '',
    emprole: '',
    amountcash: '',
    tax: 0,
    paidout: 0,
    previous_amount_cash: '',
  });


  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

 useEffect(() => {
  if (record && employees) {
    // Try multiple possible field names for amount and other fields
    const amount = record.amountcash || record.tip_out_cash || record.amount || "";
    const employeeName = record.employee || record.empname || "";
    const tax = record.tax || 0;
    
    // Find the employee role from the employees array
    let employeeRole = record.role || record.emprole || "";
    
    // If role not found in record, search in employees array
    if (!employeeRole && employeeName && Array.isArray(employees)) {
      const foundEmployee = employees.find(emp => emp.name === employeeName);
      if (foundEmployee) {
        employeeRole = foundEmployee.role;
      }
    }

    employeeRole = resolveOtherTipRole(employeeRole);
    
    setFormData({
      date: toDatetimeLocalValue(record.date),
      empname: employeeName,
      emprole: employeeRole,
      amountcash: amount.toString(),
      tax: tax.toString(),
      paidout: (parseFloat(amount) - parseFloat(tax)).toFixed(2),
      previous_amount_cash: amount.toString(),
    });
  }
}, [record, employees]); 

  const filteredEmployees = useMemo(() => {
    if (!Array.isArray(employees)) return [];
    const term = searchTerm.toLowerCase();
    return employees.filter((emp) => {
      const name = typeof emp === 'string' ? emp : emp?.name || '';
      return name.toLowerCase().includes(term);
    });
  }, [employees, searchTerm]);

  const handleEmployeeSelect = (emp) => {
    const employeeName = typeof emp === 'string' ? emp : emp.name;
    const employeeRole = typeof emp === 'string' ? 'Cashier' : resolveOtherTipRole(emp.role);
    setFormData(prev => ({ 
      ...prev, 
      empname: employeeName,
      emprole: employeeRole 
    }));
    setDropdownOpen(false);
    setSearchTerm("");
  };

  const handleClearSelection = () => {
    setFormData(prev => ({ ...prev, empname: "", emprole: "" }));
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (error) setError(null);
    
    if (name === 'amountcash') {
      const amount = parseFloat(value) || 0;
      const tax = calculateTipCommission(
        amount,
        getTipRoleFromEmployeeRole(formData.emprole),
        formData.date || record?.date
      );
      const paidout = amount - tax;
      setFormData(prev => ({ 
        ...prev, 
        tax: tax.toFixed(2),
        paidout: paidout.toFixed(2)
      }));
    }
  };

  const handleSubmit = async () => {
    if (!record) return;
    
    setError(null);
    
    // Validate required fields
    if (!formData.empname.trim()) {
      setError("Please select an employee");
      return;
    }
    
    if (!formData.emprole.trim()) {
      setError("Employee role is required");
      return;
    }
    
    // Validate amount - check for empty, null, undefined, or invalid values
    if (!formData.amountcash || formData.amountcash.trim() === "") {
      setError("Amount is required");
      return;
    }
    
    const parsedAmount = parseFloat(formData.amountcash);
    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      setError("Please enter a valid amount greater than 0");
      return;
    }
    
    setSubmitting(true);

    try {
      await onUpdate(record.id, {
        date: formData.date,
        empname: formData.empname,
        emprole: formData.emprole,
        amountcash: parsedAmount,
        owetotoast: 0,
        added_by: currentUser.username,
        previous_amount_cash: parseFloat(formData.previous_amount_cash),
      });
      onClose();
    } catch (error) {
      console.error("Error updating other tips record:", error);
      setError("Failed to update record. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen || !record) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto" onKeyDown={handleKeyDown}>
        <DialogHeader>
          <DialogTitle>Edit Tip Record</DialogTitle>
        </DialogHeader>
        
        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md border border-red-200">
            {error}
          </div>
        )}
        
        <div className="space-y-3 py-2">
          <div>
          <label className="block text-sm font-medium text-gray-700 mb-0.5">
              Date
            </label>
            <Input
              type="datetime-local"
              name="date"
              value={formData.date}
              onChange={handleInputChange}
              className="w-full"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Employee Name *
            </label>
            
            <div className="relative" ref={dropdownRef}>
              <div 
                className="flex items-center justify-between border border-gray-300 rounded-md p-2 cursor-pointer bg-white hover:border-gray-400 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500"
                onClick={() => setDropdownOpen(!dropdownOpen)}
              >
                {formData.empname ? (
                  <div className="flex items-center flex-1">
                    <span className="mr-2 text-gray-900">{formData.empname}</span>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClearSelection();
                      }}
                      className="text-gray-500 hover:text-gray-700 ml-auto"
                      aria-label="Clear selection"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <span className="text-gray-400">Select employee</span>
                )}
                <svg 
                  className={`w-4 h-4 ml-2 transform transition-transform ${dropdownOpen ? "rotate-180" : ""}`}
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </div>
              
              {dropdownOpen && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                  <div className="sticky top-0 bg-white p-2 border-b">
                    <div className="relative">
                      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
                      <Input
                        type="text"
                        placeholder="Search employees..."
                        className="pl-8"
                        value={searchTerm}
                        onChange={handleSearchChange}
                        autoFocus
                      />
                    </div>
                  </div>
                  
                  <div className="py-1">
                    {filteredEmployees.length > 0 ? (
                      filteredEmployees.map((emp, index) => {
                        const employeeName = typeof emp === 'string' ? emp : emp?.name || '';
                        const employeeRole = typeof emp === 'string' ? '' : (emp?.role || '');
                        const employeeKey = typeof emp === 'string' ? emp : emp?.id || employeeName || index;
                        return (
                        <div
                          key={employeeKey}
                          className={`flex items-center p-2 cursor-pointer hover:bg-gray-100 ${
                            formData.empname === employeeName ? "bg-blue-50" : ""
                          }`}
                          onClick={() => handleEmployeeSelect(emp)}
                        >
                          <div className={`w-5 h-5 flex items-center justify-center border rounded mr-2 ${
                            formData.empname === employeeName 
                              ? "bg-blue-500 border-blue-500" 
                              : "border-gray-300"
                          }`}>
                            {formData.empname === employeeName && (
                              <Check className="h-4 w-4 text-white" />
                            )}
                          </div>
                          <div className="flex flex-col">
                            <span className="text-gray-900">{employeeName}</span>
                            {employeeRole && (
                              <span className="text-xs text-gray-500">{employeeRole}</span>
                            )}
                          </div>
                        </div>
                        );
                      })
                    ) : (
                      <div className="p-3 text-center text-gray-500">
                        No employees found
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Position</label>
            <div className="text-sm p-2 border border-gray-300 rounded-md bg-gray-50">
              {formData.emprole || 'Select an employee'}
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Amount *
            </label>
            <Input
              type="number"
              name="amountcash"
              value={formData.amountcash || ""}
              onChange={handleInputChange}
              className="w-full"
              step="0.01"
              min="0.01"
              required
              placeholder="0.00"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tax</label>
              <div className="text-sm p-2 border border-gray-300 rounded-md text-red-500 bg-red-50">
                -{formData.tax}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Paid Out</label>
              <div className="text-sm p-2 border border-gray-300 rounded-md text-blue-500 bg-blue-50">
                {formData.paidout}
              </div>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Updated By
            </label>
            <Input
              type="text"
              value={currentUser?.username || "Current User"}
              disabled
              className="w-full bg-gray-100 text-gray-600"
            />
          </div>
        </div>
        
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <Button 
            variant="outline" 
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            className="bg-yellow-500 hover:bg-yellow-600 text-black px-4 py-2"
            disabled={submitting}
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Updating...
              </>
            ) : (
              "Update Record"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OtherTipsEditModal;